import React, { useState } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import "./Vacancies.scss";

const STEPS = ["Basic Info", "Location & Role", "Job Details", "Review"];

const initialForm = {
  jobTitle: "",
  jobType: "PERMANENT",
  employmentType: "FULL_TIME",
  noOfVacancy: 1,
  budget: "",
  expectedClosingTime: "",
  reasonOfVacancy: "",
  city: "",
  state: "",
  country: "",
  office: "",
  departmentId: "",
  roleId: "",
  interviewerId: "",
  jobDescription: "",
  responsibilities: "",
  desiredProfile: "",
  experience: "",
  skills: "",
};

const CreateVacancy = () => {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.jobTitle.trim()) e.jobTitle = "Required";
      if (!form.noOfVacancy || form.noOfVacancy < 1)
        e.noOfVacancy = "At least 1";
      if (!form.expectedClosingTime) e.expectedClosingTime = "Required";
      if (!form.reasonOfVacancy.trim()) e.reasonOfVacancy = "Required";
    }
    if (step === 1) {
      if (!form.departmentId) e.departmentId = "Required";
      if (!form.roleId) e.roleId = "Required";
    }
    if (step === 2) {
      if (!form.jobDescription.trim()) e.jobDescription = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => s + 1);
  };
  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_MANAGER_URL}/recruitment/vacancies`,
        {
          jobTitle: form.jobTitle,
          jobType: form.jobType,
          employmentType: form.employmentType,
          noOfVacancy: Number(form.noOfVacancy),
          budget: Number(form.budget) || 0,
          expectedClosingTime: new Date(form.expectedClosingTime).toISOString(),
          reasonOfVacancy: form.reasonOfVacancy,
          city: form.city,
          state: form.state,
          country: form.country,
          office: form.office,
          departmentId: Number(form.departmentId),
          roleId: Number(form.roleId),
          interviewerId: form.interviewerId ? Number(form.interviewerId) : null,
          jobDescription: form.jobDescription,
          responsibilities: form.responsibilities,
          desiredProfile: form.desiredProfile,
          experience: form.experience,
          skills: form.skills,
        },
      );
      toast.success("Vacancy request submitted for approval!");
      setForm(initialForm);
      setStep(0);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit vacancy.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Reusable field wrapper ────────────────────────────────
  const Field = ({ label, name, required, children }) => (
    <div className={`cv-field${errors[name] ? " cv-field--error" : ""}`}>
      <label className="cv-field__label">
        {label}
        {required && <span className="cv-field__req"> *</span>}
      </label>
      {children}
      {errors[name] && <span className="cv-field__error">{errors[name]}</span>}
    </div>
  );

  const inp = (name, extra = {}) => (
    <input
      className={`cv-field__input${errors[name] ? " cv-field__input--error" : ""}`}
      name={name}
      value={form[name]}
      onChange={handleChange}
      {...extra}
    />
  );

  const sel = (name, options) => (
    <select
      className="cv-field__input"
      name={name}
      value={form[name]}
      onChange={handleChange}
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );

  const ta = (name, rows = 3, placeholder = "") => (
    <textarea
      className={`cv-field__input cv-field__textarea${errors[name] ? " cv-field__input--error" : ""}`}
      name={name}
      value={form[name]}
      onChange={handleChange}
      rows={rows}
      placeholder={placeholder}
    />
  );

  // ── Step renderers ───────────────────────────────────────
  const steps = [
    // Step 0 — Basic Info
    <div className="cv-step" key="0">
      <p className="cv-step__title">Basic Information</p>
      <div className="cv-grid cv-grid--3">
        <Field label="Job Title" name="jobTitle" required>
          {inp("jobTitle", { placeholder: "e.g. Senior React Developer" })}
        </Field>
        <Field label="Job Type" name="jobType" required>
          {sel("jobType", [
            ["PERMANENT", "Permanent"],
            ["TEMPORARY", "Temporary"],
            ["CONTRACT", "Contract"],
          ])}
        </Field>
        <Field label="Employment Type" name="employmentType" required>
          {sel("employmentType", [
            ["FULL_TIME", "Full Time"],
            ["PART_TIME", "Part Time"],
            ["CONTRACTOR", "Contractor"],
            ["INTERN", "Intern"],
          ])}
        </Field>
      </div>
      <div className="cv-grid cv-grid--3">
        <Field label="No. of Positions" name="noOfVacancy" required>
          {inp("noOfVacancy", { type: "number", min: "1" })}
        </Field>
        <Field label="Budget - Upto/Max (₹)" name="budget">
          {inp("budget", { type: "number", placeholder: "e.g. 800000" })}
        </Field>
        <Field
          label="Expected Closing Date"
          name="expectedClosingTime"
          required
        >
          {inp("expectedClosingTime", { type: "datetime-local" })}
        </Field>
      </div>
      <Field label="Reason for Vacancy" name="reasonOfVacancy" required>
        {ta(
          "reasonOfVacancy",
          3,
          "Why is this position open? (New role, replacement, expansion...)",
        )}
      </Field>
    </div>,

    // Step 1 — Location & Role
    <div className="cv-step" key="1">
      <p className="cv-step__title">Location & Role</p>
      <div className="cv-grid cv-grid--3">
        <Field label="City" name="city">
          {inp("city", { placeholder: "e.g. Mumbai" })}
        </Field>
        <Field label="State" name="state">
          {inp("state", { placeholder: "e.g. Maharashtra" })}
        </Field>
        <Field label="Country" name="country">
          {inp("country", { placeholder: "e.g. India" })}
        </Field>
      </div>
      <div className="cv-grid cv-grid--3">
        <Field label="Office" name="office">
          {inp("office", { placeholder: "e.g. HQ - Andheri" })}
        </Field>
        <Field label="Department ID" name="departmentId" required>
          {inp("departmentId", {
            type: "number",
            placeholder: "Department ID",
          })}
        </Field>
        <Field label="Role ID" name="roleId" required>
          {inp("roleId", { type: "number", placeholder: "Role ID" })}
        </Field>
      </div>
      <div className="cv-grid cv-grid--2">
        <Field label="Interviewer ID" name="interviewerId">
          {inp("interviewerId", { type: "number", placeholder: "Optional" })}
        </Field>
      </div>
    </div>,

    // Step 2 — Job Details
    <div className="cv-step" key="2">
      <p className="cv-step__title">Job Details</p>
      <div className="cv-grid cv-grid--2">
        <Field label="Experience Required" name="experience">
          {inp("experience", { placeholder: "e.g. 3-5 years" })}
        </Field>
        <Field label="Skills" name="skills">
          {inp("skills", { placeholder: "e.g. React, Node.js, AWS" })}
        </Field>
      </div>
      <Field label="Job Description" name="jobDescription" required>
        {ta(
          "jobDescription",
          4,
          "Describe the role, its purpose, and what success looks like...",
        )}
      </Field>
      <div style={{ marginTop: 20 }}>
        <Field label="Responsibilities" name="responsibilities">
          {ta("responsibilities", 3, "Key day-to-day responsibilities...")}
        </Field>
      </div>
      <div style={{ marginTop: 20 }}>
        <Field label="Desired Profile" name="desiredProfile">
          {ta(
            "desiredProfile",
            3,
            "Ideal candidate background and attributes...",
          )}
        </Field>
      </div>
    </div>,

    // Step 3 — Review
    <div className="cv-step" key="3">
      <p className="cv-step__title">Review & Submit</p>
      <div className="cv-review">
        <ReviewSection title="Basic Info">
          <ReviewRow label="Job Title" value={form.jobTitle} />
          <ReviewRow label="Job Type" value={form.jobType} />
          <ReviewRow label="Employment" value={form.employmentType} />
          <ReviewRow label="Positions" value={form.noOfVacancy} />
          <ReviewRow
            label="Budget"
            value={
              form.budget ? `₹${Number(form.budget).toLocaleString()}` : "—"
            }
          />
          <ReviewRow
            label="Closing Date"
            value={form.expectedClosingTime?.split("T")[0]}
          />
          <ReviewRow label="Reason" value={form.reasonOfVacancy} full />
        </ReviewSection>
        <ReviewSection title="Location & Role">
          <ReviewRow label="City" value={form.city || "—"} />
          <ReviewRow label="State" value={form.state || "—"} />
          <ReviewRow label="Country" value={form.country || "—"} />
          <ReviewRow label="Office" value={form.office || "—"} />
          <ReviewRow label="Department ID" value={form.departmentId} />
          <ReviewRow label="Role ID" value={form.roleId} />
          {form.interviewerId && (
            <ReviewRow label="Interviewer ID" value={form.interviewerId} />
          )}
        </ReviewSection>
        <ReviewSection title="Job Details">
          <ReviewRow label="Experience" value={form.experience || "—"} />
          <ReviewRow label="Skills" value={form.skills || "—"} />
          <ReviewRow label="Description" value={form.jobDescription} full />
          {form.responsibilities && (
            <ReviewRow
              label="Responsibilities"
              value={form.responsibilities}
              full
            />
          )}
          {form.desiredProfile && (
            <ReviewRow
              label="Desired Profile"
              value={form.desiredProfile}
              full
            />
          )}
        </ReviewSection>
      </div>
    </div>,
  ];

  return (
    <div className="cv-page">
      {/* Header */}
      <div className="cv-header">
        <div>
          <h1 className="cv-header__title">Create Vacancy Request</h1>
          <p className="cv-header__sub">
            Fill in the details to raise a new vacancy for HOD & HR approval
          </p>
        </div>
        <button
          className="cv-btn cv-btn--ghost cv-btn--sm"
          onClick={() => {
            setForm(initialForm);
            setStep(0);
            setErrors({});
          }}
        >
          Reset
        </button>
      </div>

      {/* Stepper */}
      <div className="cv-stepper">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div
              className={`cv-stepper__item${i === step ? " cv-stepper__item--active" : ""}${i < step ? " cv-stepper__item--done" : ""}`}
            >
              <div className="cv-stepper__circle">
                {i < step ? (
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path
                      d="M2 6.5l3 3 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span className="cv-stepper__label">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`cv-stepper__line${i < step ? " cv-stepper__line--done" : ""}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Card */}
      <div className="cv-card">
        {steps[step]}

        <div className="cv-actions">
          {step > 0 && (
            <button className="cv-btn cv-btn--ghost" onClick={prevStep}>
              ← Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < STEPS.length - 1 ? (
            <button className="cv-btn cv-btn--primary" onClick={nextStep}>
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span className="cv-btn__spinner" />
              ) : (
                "Submit for Approval"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Helper sub-components ────────────────────────────────────────────────────
const ReviewSection = ({ title, children }) => (
  <div className="cv-review__section">
    <div className="cv-review__section-title">{title}</div>
    <div className="cv-review__grid">{children}</div>
  </div>
);

const ReviewRow = ({ label, value, full }) => (
  <div className={`cv-review__row${full ? " cv-review__row--full" : ""}`}>
    <span className="cv-review__label">{label}</span>
    <span className="cv-review__value">{value ?? "—"}</span>
  </div>
);

export default CreateVacancy;
